import { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabase";
export { CREDIT_COSTS, getCreditsForEndpoint, type CreditCheckResult } from "../utils/creditCosts";
import { checkCreditsForAction as checkCreditsPure } from "../utils/creditCosts";
import type { CreditCheckResult } from "../utils/creditCosts";

export interface CreditStatus {
  tier: string;
  sub_status: string;
  sub_end_date: string | null;
  remaining_lifetime: number;
  remaining_month: number;
  remaining_day: number;
  month_limit: number;
  day_limit: number;
  lifetime_limit: number;
  loaded: boolean;
  error: boolean;
}

const DEFAULT_STATUS: CreditStatus = {
  tier: "free",
  sub_status: "none",
  sub_end_date: null,
  remaining_lifetime: 0,
  remaining_month: 0,
  remaining_day: 0,
  month_limit: 0,
  day_limit: 0,
  lifetime_limit: 5,
  loaded: false,
  error: false,
};

export function useCredits() {
  const [status, setStatus] = useState<CreditStatus>(DEFAULT_STATUS);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc("get_credit_status");
      if (error || !data) {
        setStatus(prev => ({ ...prev, loaded: true, error: true }));
        return;
      }
      setStatus({
        tier: data.tier ?? "free",
        sub_status: data.sub_status ?? "none",
        sub_end_date: data.sub_end_date ?? null,
        remaining_lifetime: data.remaining_lifetime ?? 0,
        remaining_month: data.remaining_month ?? 0,
        remaining_day: data.remaining_day ?? 0,
        month_limit: data.month_limit ?? 0,
        day_limit: data.day_limit ?? 0,
        lifetime_limit: data.lifetime_limit ?? 5,
        loaded: true,
        error: false,
      });
    } catch {
      setStatus(prev => ({ ...prev, loaded: true, error: true }));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { status, loading, refresh };
}

export async function checkCreditsForAction(creditsNeeded: number | null): Promise<CreditCheckResult> {
  if (creditsNeeded === null || creditsNeeded <= 0) {
    return checkCreditsPure(creditsNeeded, null);
  }
  try {
    const { data, error } = await supabase.rpc("get_credit_status");
    if (error || !data) {
      return checkCreditsPure(creditsNeeded, null);
    }
    return checkCreditsPure(creditsNeeded, {
      tier: data.tier ?? "free",
      remaining_lifetime: data.remaining_lifetime ?? 0,
      remaining_month: data.remaining_month ?? 0,
      remaining_day: data.remaining_day ?? 0,
    });
  } catch {
    return checkCreditsPure(creditsNeeded, null);
  }
}
